// Le manuel d'un niveau, feuilletable dans le navigateur.
//
// Sur grand écran, le manuel se lit en doubles pages qu'on fait défiler ; chaque
// chapitre est compilé tel qu'imprimé (/web/chapitre.typ : A4, deux colonnes,
// bandeau) quand on approche de ses pages. Sur petit écran, les exercices sont compilés un par un
// (/web/exercice.typ) et se suivent à la verticale, à mesure qu'on fait défiler
// la page.
//
// Sur grand écran, un clic sur un exercice l'ouvre dans une fenêtre, où l'on
// change sa graine et affiche son corrigé (fenetre.js). Sur petit écran, les
// réglages, dépliés depuis l'en-tête, valent pour tous les exercices : corrigés
// affichés ou non, graines tirées au hasard ou celles du manuel imprimé.
//
// Le niveau est donné par la page : <main data-niveau="3e">. Point d'entrée des
// pages des niveaux (6eme/, 5eme/, 3eme/).

import { type Diagnostic, démarrer, nomExercice, type Outils, texteErreurs } from './compilateur.ts';
import { fenêtreExercice } from './fenetre.ts';
import type { Chapitre, Exercice, Manuel } from './types.ts';

const $ = <E extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as E;
const racine = document.querySelector('[data-niveau]') as HTMLElement;
const sommaire = $<HTMLSelectElement>('sommaire');
const état = $('etat');
const petitÉcran = matchMedia('(max-width: 60rem)');

let outils: Outils;
let manuel: Manuel;

// Un chapitre ou un exercice d'après son rang (data-chapitre, data-exercice,
// valeur du sommaire) : toujours un rang valide, posé par ce script.
const chapitreN = (i: number | string | undefined) => manuel.chapitres[Number(i)] as Chapitre;
const exerciceN = (c: Chapitre, j: number | string | undefined) => c.exercices[Number(j)] as Exercice;

const deux = (n: number) => String(n).padStart(2, '0');
const élément = <K extends keyof HTMLElementTagNameMap>(balise: K, classe: string, texte?: string) =>
  Object.assign(document.createElement(balise), { className: classe, textContent: texte ?? '' });

const blocErreurs = (diagnostics: Diagnostic[]) => élément('pre', 'erreurs', texteErreurs(diagnostics));
const couleur = (c: Chapitre) => manuel.couleurs[c.thème] ?? 'var(--noir)';

// Un bouton invisible posé sur l'exercice j du chapitre i, qui l'ouvre dans la
// fenêtre ; il ne se révèle qu'au survol.
function zoneExercice(i: number, j: number) {
  const c = chapitreN(i);
  const e = exerciceN(c, j);
  const zone = élément('button', 'zone-exercice');
  zone.type = 'button';
  Object.assign(zone.dataset, { chapitre: i, exercice: j });
  zone.setAttribute('aria-haspopup', 'dialog');
  zone.title = 'Changer la graine, voir le corrigé';
  zone.setAttribute('aria-label', `${nomExercice(c.num, e.n)}. ${e.titre} : changer la graine, voir le corrigé`);
  zone.style.setProperty('--theme', couleur(c));
  return zone;
}

// Le chapitre courant se lit et s'écrit dans l'URL (?chapitre=7), pour qu'un
// lien ouvre le manuel au bon endroit.
function lireURL() {
  const q = new URLSearchParams(location.search);
  return Math.max(
    manuel.chapitres.findIndex((c) => String(c.num) === q.get('chapitre')),
    0,
  );
}

function écrireURL(chapitre: number) {
  history.replaceState(null, '', `?${new URLSearchParams({ chapitre: String(chapitreN(chapitre).num) })}`);
}

// Le sommaire (et l'URL) suivent le chapitre qui occupe le milieu de l'écran.
const repère = new IntersectionObserver(
  (entrées) => {
    for (const { target, isIntersecting } of entrées) {
      const { chapitre } = (target as HTMLElement).dataset;
      if (!isIntersecting || fil.cible || chapitre === undefined) continue;
      sommaire.value = chapitre;
      écrireURL(Number(chapitre));
    }
  },
  { rootMargin: '-50% 0px -50% 0px' },
);

// ── Grand écran : le manuel en doubles pages ─────────────────────────────────

// Toutes les pages sont posées d'emblée, vides : la pagination de chaque chapitre
// est connue d'avance (catalogue.json, cf. construire.ts). Un chapitre est
// compilé tel qu'imprimé (/web/chapitre.typ), à partir de sa première page, quand
// on approche de ses pages.
const livre = {
  prêt: false,
  conteneur: $('livre'),
  défs: $('defs'),
  feuilles: [] as HTMLElement[][], // chapitre → ses feuilles
  proches: new Set<HTMLElement>(), // feuilles à l'écran ou presque, de chapitres pas encore compilés
  enCours: false,
};

// Une compilation rend toutes les pages d'un chapitre dans un seul SVG : on garde
// à part les définitions communes (glyphes, styles), insérées une fois dans la
// page, et chaque page devient un SVG autonome qui y fait référence.
function découper(svg: string) {
  const boîte = document.createElement('div');
  boîte.innerHTML = svg;
  const doc = boîte.firstElementChild as SVGSVGElement;
  const pages = [...doc.querySelectorAll<SVGGElement>(':scope > g.typst-page')].map((g) => {
    const l = g.dataset.pageWidth;
    const h = g.dataset.pageHeight;
    g.removeAttribute('transform');
    const page = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    page.setAttribute('viewBox', `0 0 ${l} ${h}`);
    page.setAttribute('class', 'typst-doc');
    page.append(g);
    return page;
  });
  for (const s of doc.querySelectorAll(':scope > script')) s.remove();
  doc.setAttribute('width', '0');
  doc.setAttribute('height', '0');
  doc.removeAttribute('viewBox');
  livre.défs.append(doc);
  return pages;
}

// Position à l'écran d'un repère : son coin gauche et sa largeur. Calculée par sa
// matrice plutôt que par getBoundingClientRect(), qui rend un rectangle vide pour
// une forme de hauteur nulle dans Firefox.
function positionRepère(rect: SVGRectElement) {
  const m = rect.getScreenCTM() ?? undefined;
  const coin = new DOMPoint(0, 0).matrixTransform(m);
  const bout = new DOMPoint(rect.width.baseVal.value, 0).matrixTransform(m);
  return { left: coin.x, top: coin.y, width: bout.x - coin.x };
}

// Rend cliquables les exercices d'une feuille du chapitre i. Chaque exercice est
// encadré de deux liens de hauteur nulle, « exercice:7/4/haut » et « …/bas »
// (`repères-exercices` dans commun.typ) : on en tire sa zone, en proportion de la
// feuille pour qu'elle suive les changements de taille, puis on les retire.
type Position = ReturnType<typeof positionRepère>;

function zonesExercices(feuille: HTMLElement, i: number) {
  const c = chapitreN(i);
  const cadre = feuille.getBoundingClientRect();
  const repères = new Map<string, { haut?: Position; bas?: Position }>();
  for (const a of feuille.querySelectorAll('a')) {
    const [, n, bord] = a.getAttribute('xlink:href')?.match(/^exercice:\d+\/(\d+)\/(haut|bas)$/) ?? [];
    if (!n || !bord) continue;
    repères.set(n, { ...repères.get(n), [bord]: positionRepère(a.firstElementChild as SVGRectElement) });
    a.remove();
  }
  // Un peu d'air autour de l'exercice : le badge de son numéro déborde du haut.
  const marge = (cadre.width * 4) / 595;
  const pc = (v: number, total: number) => `${(100 * v) / total}%`;
  for (const [n, { haut, bas }] of repères) {
    const j = c.exercices.findIndex((e) => e.n === Number(n));
    if (!haut || !bas || j < 0) continue;
    const zone = zoneExercice(i, j);
    Object.assign(zone.style, {
      left: pc(haut.left - cadre.left - marge, cadre.width),
      top: pc(haut.top - cadre.top - marge, cadre.height),
      width: pc(haut.width + 2 * marge, cadre.width),
      height: pc(bas.top - haut.top + 2 * marge, cadre.height),
    });
    feuille.append(zone);
  }
}

// Pose une feuille à la suite : à gauche d'une nouvelle double page, ou à droite
// de la dernière.
function poser() {
  let double = livre.conteneur.lastElementChild;
  if (!double || double.childElementCount === 2) {
    double = élément('div', 'double-page');
    livre.conteneur.append(double);
  }
  const f = élément('div', `feuille ${double.childElementCount ? 'droite' : 'gauche'} blanche`);
  double.append(f);
  return f;
}

function construireLivre() {
  for (const [i, c] of manuel.chapitres.entries()) {
    livre.feuilles[i] = Array.from({ length: c.pages }, (_, k) => {
      const f = poser();
      f.dataset.chapitre = String(i);
      if (k === 0) f.append(élément('p', 'attente', `Chapitre ${deux(c.num)} — mise en page…`));
      repère.observe(f);
      approche.observe(f);
      return f;
    });
  }
  if (livre.conteneur.lastElementChild?.childElementCount === 1) poser();
}

// Compile les chapitres proches de l'écran : celui qu'on lit d'abord, puis les
// autres dans l'ordre du livre.
async function compilerChapitres() {
  if (livre.enCours) return;
  livre.enCours = true;
  try {
    for (;;) {
      const lu = Number(sommaire.value);
      const chapitres = new Set([...livre.proches].map((f) => Number(f.dataset.chapitre)));
      const [i] = [...chapitres].sort((a, b) => (a === lu ? -1 : b === lu ? 1 : a - b));
      if (i === undefined) break;
      const feuilles = livre.feuilles[i] ?? [];
      for (const f of feuilles) {
        livre.proches.delete(f);
        approche.unobserve(f);
      }
      const c = chapitreN(i);
      const t0 = performance.now();
      const { svg, erreurs } = await outils.compiler('/web/chapitre.typ', {
        chapitre: c.chemin,
        thèmes: manuel.thèmes,
        graine: String(manuel.graine),
        page: String(c.page),
      });
      const pages = erreurs ? [blocErreurs(erreurs)] : découper(svg);
      if (!erreurs) console.info(`[livre] chapitre ${c.num} compilé en ${Math.round(performance.now() - t0)} ms`);
      if (pages.length !== feuilles.length && !erreurs) {
        console.warn(
          `[livre] chapitre ${c.num} : ${pages.length} pages au lieu des ${feuilles.length} prévues (relancer bun construire)`,
        );
      }
      for (const [k, f] of feuilles.entries()) {
        const page = pages[k];
        f.replaceChildren(...(page ? [page] : []));
        f.classList.toggle('blanche', !page);
        if (page && !erreurs) zonesExercices(f, i);
      }
    }
  } finally {
    livre.enCours = false;
  }
}

const approche = new IntersectionObserver(
  (entrées) => {
    for (const { target, isIntersecting } of entrées) {
      if (isIntersecting) livre.proches.add(target as HTMLElement);
      else livre.proches.delete(target as HTMLElement);
    }
    compilerChapitres();
  },
  { rootMargin: '100% 0px' },
);

function ouvrirLivre(chapitre: number) {
  if (!livre.prêt) {
    livre.prêt = true;
    construireLivre();
  }
  sommaire.value = String(chapitre);
  état.textContent = '';
  if (chapitre > 0 || scrollY > 0) livre.feuilles[chapitre]?.[0]?.parentElement?.scrollIntoView();
}

// ── Petit écran : les exercices un par un, à la verticale ──────────────────────

const fil = {
  prêt: false,
  conteneur: $('fil'),
  proches: new Set<HTMLElement>(), // exercices à l'écran ou presque, pas encore compilés
  enCours: false,
  sections: [] as HTMLElement[],
  // Chapitre choisi au sommaire : les exercices compilés au-dessus grandissent et
  // le pousseraient vers le bas, on l'y ramène jusqu'à ce que le lecteur défile.
  cible: null as HTMLElement | null,
  corrigés: false,
  graines: null as Map<Exercice, number> | null, // exercice → graine tirée au hasard ; null : celles du manuel
};

// Graine et corrigé d'un exercice, selon les réglages du moment.
function réglagesFil(e: Exercice) {
  if (fil.graines && !fil.graines.has(e)) fil.graines.set(e, Math.floor(Math.random() * 1_000_000));
  return { graine: String(fil.graines?.get(e) ?? e.graine), corrigé: fil.corrigés ? 'oui' : 'non' };
}
const clé = ({ graine, corrigé }: ReturnType<typeof réglagesFil>) => `${graine}/${corrigé}`;

// Les réglages ont changé : chaque exercice déjà compilé garde son rendu, estompé,
// jusqu'à ce qu'on le recompile en approchant de l'écran.
function recompilerFil() {
  if (!fil.prêt) return;
  for (const ex of fil.conteneur.querySelectorAll('.fil-exercice.compilé')) {
    ex.classList.add('périmé');
    observateur.observe(ex);
  }
}

const viser = () => fil.cible?.scrollIntoView();
for (const geste of ['wheel', 'touchstart', 'keydown']) {
  window.addEventListener(
    geste,
    () => {
      fil.cible = null;
    },
    { passive: true },
  );
}

// Compile les exercices proches de l'écran, dans l'ordre du livre. Un exercice
// dépassé avant son tour reste en attente jusqu'à ce qu'on y revienne.
async function compilerProches() {
  if (fil.enCours) return;
  fil.enCours = true;
  try {
    for (;;) {
      const [suivant] = [...fil.proches].sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
      );
      if (!suivant) break;
      fil.proches.delete(suivant);
      observateur.unobserve(suivant);
      const c = chapitreN(suivant.dataset.chapitre);
      const e = exerciceN(c, suivant.dataset.exercice);
      const réglages = réglagesFil(e);
      const { svg, erreurs } = await outils.compiler('/web/exercice.typ', {
        exercice: e.chemin,
        thèmes: manuel.thèmes,
        thème: c.thème,
        chapitre: String(c.num),
        numéro: String(e.n),
        ...réglages,
      });
      if (erreurs) suivant.replaceChildren(blocErreurs(erreurs));
      else suivant.innerHTML = svg;
      suivant.removeAttribute('aria-busy');
      suivant.classList.add('compilé');
      // Réglages changés pendant la compilation : l'exercice reste à refaire.
      if (clé(réglages) === clé(réglagesFil(e))) suivant.classList.remove('périmé');
      else observateur.observe(suivant);
      viser();
    }
  } finally {
    fil.enCours = false;
  }
}

const observateur = new IntersectionObserver(
  (entrées) => {
    for (const { target, isIntersecting } of entrées) {
      if (isIntersecting) fil.proches.add(target as HTMLElement);
      else fil.proches.delete(target as HTMLElement);
    }
    compilerProches();
  },
  { rootMargin: '1200px 0px' },
);

function construireFil() {
  for (const [i, c] of manuel.chapitres.entries()) {
    const section = élément('section', 'fil-chapitre');
    section.dataset.chapitre = String(i);
    section.style.setProperty('--theme', couleur(c));
    const titre = élément('h2', 'fil-titre');
    titre.append(élément('span', 'fil-numero', `Chapitre ${deux(c.num)}`), c.titre);
    section.append(titre);
    let partie: string | null = null;
    for (const [j, e] of c.exercices.entries()) {
      if (e.partie !== partie && e.partie) section.append(élément('h3', 'fil-partie', e.partie));
      partie = e.partie;
      // En attendant la compilation : le numéro et le titre, à la place de l'exercice.
      const ex = élément('article', 'fil-exercice');
      Object.assign(ex.dataset, { chapitre: i, exercice: j });
      ex.setAttribute('aria-busy', 'true');
      ex.append(élément('span', 'fil-badge', nomExercice(c.num, e.n)), élément('span', 'fil-nom', e.titre));
      section.append(ex);
      observateur.observe(ex);
    }
    fil.sections.push(section);
    repère.observe(section);
  }
  fil.conteneur.replaceChildren(...fil.sections);
}

function ouvrirFil(chapitre: number) {
  if (!fil.prêt) {
    fil.prêt = true;
    construireFil();
  }
  sommaire.value = String(chapitre);
  état.textContent = '';
  fil.cible = chapitre > 0 ? (fil.sections[chapitre] ?? null) : null;
  viser();
}

// ── Branchements ─────────────────────────────────────────────────────────────

function brancherRéglages() {
  const options = $<HTMLButtonElement>('options');
  const réglages = $<HTMLFormElement>('reglages');
  const corrigés = $<HTMLInputElement>('corriges');
  options.disabled = false;
  options.addEventListener('click', () => {
    réglages.hidden = !réglages.hidden;
    options.setAttribute('aria-expanded', String(!réglages.hidden));
  });
  réglages.addEventListener('submit', (ev) => ev.preventDefault());
  corrigés.addEventListener('change', () => {
    fil.corrigés = corrigés.checked;
    recompilerFil();
  });
  $('hasard').addEventListener('click', () => {
    fil.graines = new Map();
    recompilerFil();
  });
  $('graines-manuel').addEventListener('click', () => {
    if (!fil.graines) return;
    fil.graines = null;
    recompilerFil();
  });
}

function ouvrir(chapitre: number) {
  racine.dataset.mode = petitÉcran.matches ? 'fil' : 'livre';
  if (petitÉcran.matches) ouvrirFil(chapitre);
  else ouvrirLivre(chapitre);
}

try {
  outils = await démarrer();
  const trouvé = outils.catalogue.manuels.find((m) => m.id === racine.dataset.niveau);
  if (!trouvé) throw new Error(`niveau inconnu : ${racine.dataset.niveau}`);
  manuel = trouvé;
  sommaire.replaceChildren(
    ...manuel.chapitres.map((c, i) =>
      Object.assign(document.createElement('option'), { value: String(i), textContent: `${deux(c.num)} — ${c.titre}` }),
    ),
  );
  sommaire.disabled = false;
  brancherRéglages();
  const ouvrirExercice = fenêtreExercice(outils, manuel);
  document.addEventListener('click', (ev) => {
    const zone = (ev.target as Element).closest<HTMLElement>('.zone-exercice');
    if (!zone) return;
    const c = chapitreN(zone.dataset.chapitre);
    ouvrirExercice(c, exerciceN(c, zone.dataset.exercice), zone);
  });
  sommaire.addEventListener('change', () => {
    écrireURL(Number(sommaire.value));
    ouvrir(Number(sommaire.value));
  });
  // En passant d'un format d'écran à l'autre, on reste dans le même chapitre.
  petitÉcran.addEventListener('change', () => ouvrir(Number(sommaire.value)));
  ouvrir(lireURL());
} catch (err) {
  console.error(err);
  état.textContent = `Impossible de charger le compilateur : ${err instanceof Error ? err.message : err}`;
}
